import { hourToNumber, numberToHour } from '@/utils/time';
import {
    Location,
    Timeline,
    TimeUnit,
    WeekDay,
    Workshop,
} from '@/modules/workshops-schedule/models/weekScheduleModels';
import { List } from 'ts-generic-collections-linq';
import ICalendarData from '@/modules/calendar/interfaces/ICalendarData';

export class SheduleBoardWeekDaysAdjuster {
    getFilteredWeekDays(events: any[]) {
        const groups = new List<ICalendarData>(events)
            .where(
                x =>
                    x.GroupType == 'path' ||
                    x.GroupType == 'masterclass' ||
                    x.GroupType == 'open' ||
                    x.GroupType == 'practice' ||
                    x.GroupType == 'party' ||
                    x.GroupType == 'workshop',
            )
            .where(x => new Date(x.EndDate) >= new Date());
        const weekDayNames: List<{ DayNumber: number; Name: string }> = new List([
            { DayNumber: 2, Name: 'Monday' },
            { DayNumber: 3, Name: 'Tuesday' },
            { DayNumber: 4, Name: 'Wednesday' },
            { DayNumber: 5, Name: 'Thursday' },
            { DayNumber: 6, Name: 'Friday' },
            { DayNumber: 7, Name: 'Saturday' },
            { DayNumber: 1, Name: 'Sunday' },
        ]);
        const calssrooms: List<{ Id: number; Name: string }> = new List<{ Id: number; Name: string }>([
            { Id: 1, Name: 'Main Room' },
            { Id: 2, Name: 'Blind Room' },
            { Id: 3, Name: 'Chill Room' },
            { Id: 4, Name: 'Room 4' },
            { Id: 5, Name: 'Room 5' },
            { Id: 6, Name: 'Room 6' },
            { Id: 7, Name: 'Hall' },
        ]);
        const weekDays: WeekDay[] = groups
            .select(group => ({
                Id: group.WeekDayId,
                Name: weekDayNames.firstOrDefault(wdn => wdn.DayNumber === group.WeekDayNumber)?.Name ?? '',
                Timeline: {
                    Title: '',
                    TimelineStartTime: '',
                    TimelineEndTime: '',
                    TimeUnits: [],
                },
                Locations: groups
                    .where(g => g.WeekDayId == group.WeekDayId)
                    .groupBy(g => [g.Address])
                    .select<Location>(address => ({
                        Name: address.groups[0],
                        Rooms: groups
                            .where(g => g.WeekDayId == group.WeekDayId && g.Address == address.groups[0])
                            .groupBy(g => [g.ClassroomId])
                            .select(g => ({
                                Name: (
                                    calssrooms.firstOrDefault(c => c.Id == g.groups[0]) ?? {
                                        Id: 0,
                                        Name: g.groups[0].toString(),
                                    }
                                ).Name,
                                Workshops: groups
                                    .where(
                                        gr =>
                                            gr.WeekDayId == group.WeekDayId &&
                                            gr.Address == address.groups[0] &&
                                            gr.ClassroomId == g.groups[0],
                                    )
                                    .select(gr => ({
                                        Id: 0,
                                        WorkshopKey: gr.Id,
                                        Title: gr.Name,
                                        InstructorName: gr.InstructorName,
                                        Image: gr.InstructorImageUrl,
                                        VideoUrl: gr.VideoUrl,
                                        // Image: gr.InstructorThumbnailUrl,
                                        Start: gr.StartTime,
                                        End: gr.EndTime,
                                        Level: gr.AdvanceLevel,
                                        Description: gr.Description,
                                        WorkshopType: gr.GroupType,
                                        HasDedicatedTicket: gr.HasDedicatedTicket,
                                        IncludedInTickets: gr.IncludedInTickets,
                                    }))
                                    .toArray(),
                            }))
                            .toArray()
                            .sort((a, b) => (a.Name > b.Name ? 1 : 0)),
                    }))
                    .toArray(),
            }))
            .groupBy(x => [x.Name])
            .select(x => x.list.first())
            .toArray()
            .sort((a, b) => (a.Id > b.Id ? 1 : 0));

        return weekDays;
    }
    public getTimeUnits(weekDays: WeekDay[]): TimeUnit[] {
        const scheduleBufferSize = 1; // 0.5 => 30 min, 1 => 1 h
        let timeUnits: TimeUnit[] = [];
        const timeUnitDuration = this.timeUnitDuration();
        if (weekDays.length > 0) {
            const firstHourInWeek = this.getFirstHourInWeek(weekDays);
            const lastHourInWeek = this.getLastHourInWeek(weekDays);
            const sequenceStart = hourToNumber(firstHourInWeek) - scheduleBufferSize;
            const sequenceEnd = hourToNumber(lastHourInWeek) + timeUnitDuration + scheduleBufferSize;
            for (let x = sequenceStart; x <= sequenceEnd; x += timeUnitDuration) {
                timeUnits.push({ Value: numberToHour(x) });
            }
        } else {
            timeUnits = [{ Value: '' }];
        }

        return timeUnits;
    }

    public timeUnitDuration(): number {
        // return 0.5; // ues for half of hour
        return 1; // use for whole hour
    }

    public createTimelineFor(weekDay: WeekDay, timeUnits: TimeUnit[]): Timeline {
        // const timeUnits = this.getTimeUnits([weekDay]);
        return {
            Title: '',
            TimeUnits: timeUnits,
            TimelineStartTime: timeUnits[0].Value,
            TimelineEndTime: numberToHour(
                hourToNumber(timeUnits[timeUnits.length - 1].Value) + this.timeUnitDuration(),
            ),
        };
    }

    public setBufferingWorkshops(weekDays: WeekDay[], timeline: Timeline) {
        const firstHourInWeek = timeline.TimelineStartTime;
        const lastHourInWeek = timeline.TimelineEndTime;
        let bufferWorkshopId = 100001;
        weekDays.forEach(weekDay => {
            this.addBufferWorkshops(weekDay, firstHourInWeek, lastHourInWeek);
            const workshops = weekDay.Locations.map((l: Location) => l.Rooms)
                .reduce((a, b) => a.concat(b), [])
                .map(r => r.Workshops)
                .reduce((a, b) => a.concat(b), []);
            workshops.forEach((workshop: Workshop) => {
                workshop.Height = this.calculateWorkshopBoxHeight(workshop, weekDays);
                if (workshop.IsVisible === undefined) {
                    workshop.IsVisible = true;
                }
                if (workshop.Id === undefined) {
                    workshop.Id = bufferWorkshopId++;
                }
            });
        });
    }

    public calculateWorkshopBoxHeight(workshop: Workshop, weekDays: WeekDay[]) {
        const workshopStart = workshop.Start;
        const workshopEnd = workshop.End;
        const timeUnits = this.getTimeUnits(weekDays);
        const totalNumberOfTimeUnits = this.getTotalNumberOfTimeUnits(timeUnits);
        const timeUnitDuration = this.timeUnitDuration();
        const numberOfTimeUnitsInClass = (hourToNumber(workshopEnd) - hourToNumber(workshopStart)) / timeUnitDuration;
        return ((100 / totalNumberOfTimeUnits) * numberOfTimeUnitsInClass).toString() + '%';
    }

    public calculateTimeUnitBoxHeight(workshop: Workshop, weekDays: WeekDay[]) {
        const timeUnits = this.getTimeUnits(weekDays);
        const totalNumberOfTimeUnits = this.getTotalNumberOfTimeUnits(timeUnits);
        return (100 / totalNumberOfTimeUnits).toString() + '%';
    }

    private getTotalNumberOfTimeUnits(timeUnits: TimeUnit[]) {
        return timeUnits.length;
    }

    private getFirstHourInDay(weekDay: WeekDay) {
        const firstClass = weekDay.Locations.map(l => l.Rooms)
            .reduce((a, b) => a.concat(b), [])
            .map(r => r.Workshops)
            .reduce((a, b) => a.concat(b), [])
            .reduce((a, b) => (hourToNumber(a.Start) < hourToNumber(b.Start) ? a : b));

        return firstClass.Start;
    }

    private getLastHourInDay(weekDay: WeekDay) {
        const lastClass = weekDay.Locations.map(l => l.Rooms)
            .reduce((a, b) => a.concat(b), [])
            .map(r => r.Workshops)
            .reduce((a, b) => a.concat(b), [])
            .reduce((a, b) => (hourToNumber(a.Start) > hourToNumber(b.Start) ? a : b));

        return lastClass.End;
    }

    private getFirstHourInWeek(weekDays: WeekDay[]) {
        const workshops = weekDays
            .map(l => l.Locations)
            .reduce((a, b) => a.concat(b), [])
            .map(l => l.Rooms)
            .reduce((a, b) => a.concat(b), [])
            .map(r => r.Workshops)
            .reduce((a, b) => a.concat(b), []);
        const visibleWorkshops = workshops.filter(w => w.IsVisible !== false);
        const firstClass = visibleWorkshops.length
            ? visibleWorkshops.reduce((a, b) => (hourToNumber(a.Start) < hourToNumber(b.Start) ? a : b))
            : workshops.reduce((a, b) => (hourToNumber(a.Start) < hourToNumber(b.Start) ? a : b));

        return firstClass.Start;
    }

    private getLastHourInWeek(weekDays: WeekDay[]) {
        const workshops = weekDays
            .map(l => l.Locations)
            .reduce((a, b) => a.concat(b), [])
            .map(l => l.Rooms)
            .reduce((a, b) => a.concat(b), [])
            .map(r => r.Workshops)
            .reduce((a, b) => a.concat(b), []);
        const visibleWorkshops = workshops.filter(w => w.IsVisible !== false);
        const lastClass = visibleWorkshops.length
            ? visibleWorkshops.reduce((a, b) => (hourToNumber(a.Start) > hourToNumber(b.Start) ? a : b))
            : workshops.reduce((a, b) => (hourToNumber(a.Start) > hourToNumber(b.Start) ? a : b));

        return lastClass.Start;
    }

    public addBufferWorkshops(weekDay: WeekDay, firstHourInWeek?: string, lastHourInWeek?: string): WeekDay {
        const firstHourInDay = this.getFirstHourInDay(weekDay);
        if (firstHourInWeek !== undefined && hourToNumber(firstHourInWeek) < hourToNumber(firstHourInDay)) {
            const startBufferWorkshop: Workshop = {
                Id: 0,
                Start: firstHourInWeek,
                End: firstHourInDay,
                IsVisible: false,
                HasDedicatedTicket: false,
                IncludedInTickets: [],
            };
            weekDay.Locations.forEach(location =>
                location.Rooms.forEach(room => room.Workshops.unshift(startBufferWorkshop)),
            );
        }

        weekDay.Locations.forEach(location =>
            location.Rooms.forEach(room => {
                const sortedWorkshops = room.Workshops.sort(function(a, b) {
                    return hourToNumber(a.Start) < hourToNumber(b.Start) ? -1 : 1;
                });
                for (let i = 0; i < sortedWorkshops.length; i++) {
                    if (i + 1 === sortedWorkshops.length || sortedWorkshops[i].End === sortedWorkshops[i + 1].Start) {
                        continue;
                    }

                    const workshopBuffer: Workshop = {
                        Id: 0,
                        Start: sortedWorkshops[i].End,
                        End: sortedWorkshops[i + 1].Start,
                        IsVisible: false,
                        HasDedicatedTicket: false,
                        IncludedInTickets: [],
                    };

                    room.Workshops.splice(i + 1, 0, workshopBuffer);
                }
            }),
        );

        const lastHourInDay = this.getLastHourInDay(weekDay);
        if (lastHourInWeek !== undefined && hourToNumber(lastHourInWeek) > hourToNumber(lastHourInDay)) {
            const endBufferWorkshop: Workshop = {
                Id: 0,
                Start: lastHourInDay,
                End: lastHourInWeek,
                IsVisible: false,
                HasDedicatedTicket: false,
                IncludedInTickets: [],
            };
            weekDay.Locations.forEach(location =>
                location.Rooms.forEach(room => room.Workshops.push(endBufferWorkshop)),
            );
        }

        return weekDay;
    }
}
